<script setup lang="ts">
import { computed, markRaw, ref, watch } from "vue";
import {
  BriefcaseBusiness,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  ClipboardList,
  Home,
  LogOut,
  PlusCircle,
  Settings2,
  Sparkles,
  Users,
  ShieldCheck,
  Handshake,
  Menu,
  MoreHorizontal,
  X,
} from "lucide-vue-next";
import { useI18n } from "vue-i18n";
import { useSessionStore } from "@/stores/session";
import { useBriefStore } from "@/stores/briefs";

const props = defineProps<{ view: string }>();
const emit = defineEmits<{ changeView: [view: string] }>();
const { t, locale } = useI18n();
const session = useSessionStore();
const briefs = useBriefStore();
const isClient = computed(() => session.user?.role === "client");
const isPartner = computed(() => isClient.value && session.user?.account_type === "partner");
const compactMobileHeader = computed(() =>
  isClient.value && (props.view === "briefs" || props.view.startsWith("brief:")),
);
const mobileHeaderExpanded = ref(false);
const mobileMenuOpen = ref(false);
const navItems = computed(() =>
  isClient.value
      ? [
        { id: "home", label: t("shell.home"), icon: markRaw(Home) },
        { id: "briefs", label: t("newBrief"), icon: markRaw(PlusCircle) },
        { id: "tasks", label: t("shell.tasks"), icon: markRaw(ClipboardList) },
      ]
      : [
        { id: "work", label: t("workbench"), icon: markRaw(BriefcaseBusiness) },
        { id: "tasks", label: t("shell.tasks"), icon: markRaw(ClipboardList) },
        { id: "partner-requests", label: t("shell.partnerRequests"), icon: markRaw(Handshake) },
        ...(session.user?.role === "admin"
          ? [
              { id: "admin-workflow", label: t("shell.dispatch"), icon: markRaw(ShieldCheck) },
              { id: "members", label: t("members"), icon: markRaw(Users) },
              { id: "model", label: t("model"), icon: markRaw(Settings2) },
              { id: "skills", label: "Skills", icon: markRaw(Sparkles) },
            ]
          : []),
      ],
);
const mobileNavItems = computed(() => {
  const preferred = isClient.value
    ? ["home", "briefs", "tasks"]
    : session.user?.role === "admin"
      ? ["work", "tasks", "admin-workflow"]
      : ["work", "tasks", "partner-requests"];
  return preferred
    .map((id) => navItems.value.find((item) => item.id === id))
    .filter((item): item is NonNullable<typeof item> => Boolean(item));
});
const mobileOverflowItems = computed(() =>
  navItems.value.filter((item) => !mobileNavItems.value.some((primary) => primary.id === item.id)),
);
const currentLabel = computed(() => {
  if (props.view.startsWith("brief:")) return t("myBriefs");
  return navItems.value.find((item) => item.id === props.view)?.label || t("app");
});
watch(() => props.view, () => {
  mobileHeaderExpanded.value = false;
  mobileMenuOpen.value = false;
});
function changeView(view: string) {
  mobileMenuOpen.value = false;
  emit("changeView", view);
}
function changeLanguage(value: string) {
  locale.value = value;
  void session.setLocale(value);
}
async function removeBrief(id: number) { if (!window.confirm(t('shell.deleteBrief'))) return; await briefs.deleteBrief(id); if (props.view === `brief:${id}`) emit('changeView', 'home') }
</script>

<template>
  <div
    class="shell"
    :class="{
      'shell--detail': compactMobileHeader,
      'shell--header-expanded': mobileHeaderExpanded,
    }"
  >
    <header class="topbar">
      <button
        v-if="compactMobileHeader"
        class="mobile-header-toggle"
        type="button"
        :title="mobileHeaderExpanded ? t('shell.collapse') : t('shell.expand')"
        :aria-label="mobileHeaderExpanded ? t('shell.collapse') : t('shell.expand')"
        @click="mobileHeaderExpanded = !mobileHeaderExpanded"
      >
        <ChevronUp v-if="mobileHeaderExpanded" :size="18" />
        <ChevronDown v-else :size="18" />
      </button>
      <div class="brand">
        <img src="/jaohui-logo.webp" alt="Jaohui" /><b>{{ t("app") }}</b>
      </div>
      <strong class="mobile-view-title">{{ currentLabel }}</strong>
      <div class="topbar__actions">
        <select
          v-if="isClient"
          class="locale"
          :value="locale"
          aria-label="Language"
          @change="changeLanguage(($event.target as HTMLSelectElement).value)"
        >
          <option value="zh-CN">中文</option>
          <option value="en">English</option>
          <option value="fr">Francais</option>
        </select>
        <span class="status">{{
          session.user?.role === "client"
            ? isPartner ? t("shell.partner") : t("shell.client")
            : session.user?.role === "staff"
              ? t("shell.staff")
              : t("shell.admin")
        }}</span>
        <span class="user-name">{{ session.user?.display_name }}</span>
        <button class="btn logout" :title="t('logout')" @click="session.logout"><LogOut :size="16" /><span>{{ t("logout") }}</span></button>
      </div>
      <button class="mobile-menu-button" type="button" :aria-label="t('shell.more')" @click="mobileMenuOpen = true"><Menu :size="22" /></button>
    </header>
    <div class="shell__body">
      <nav class="nav nav--desktop">
        <button
          v-for="item in navItems"
          :key="item.id"
          class="nav__item"
          :class="{ active: props.view === item.id }"
          @click="changeView(item.id)"
        >
          <component :is="item.icon" :size="18" /><span>{{ item.label }}</span>
        </button>
        <section v-if="isClient" class="brief-nav">
          <small>{{ t("myBriefs") }}</small
          ><div v-for="brief in briefs.briefs" :key="brief.id" class="brief-nav__row"><button class="brief-nav__item" :class="{ active: props.view === `brief:${brief.id}` }" :title="brief.title" @click="changeView(`brief:${brief.id}`)">{{ brief.title }}</button><button class="brief-nav__delete" :title="t('shell.deleteNamed', { title: brief.title })" @click="removeBrief(brief.id)">{{ t("common.delete") }}</button></div>
        </section>
      </nav>
      <main class="main"><slot /></main>
    </div>
    <nav class="mobile-nav" :style="{ '--mobile-nav-count': mobileNavItems.length + 1 }" aria-label="Mobile navigation">
      <button v-for="item in mobileNavItems" :key="item.id" class="mobile-nav__item" :class="{ active: props.view === item.id || (item.id === 'home' && props.view.startsWith('brief:')) }" type="button" @click="changeView(item.id)"><component :is="item.icon" :size="20" /><span>{{ item.label }}</span></button>
      <button class="mobile-nav__item" :class="{ active: mobileMenuOpen || mobileOverflowItems.some((item) => item.id === props.view) }" type="button" @click="mobileMenuOpen = true"><MoreHorizontal :size="21" /><span>{{ t("shell.more") }}</span></button>
    </nav>

    <Teleport to="body">
      <div v-if="mobileMenuOpen" class="mobile-drawer-backdrop" @click.self="mobileMenuOpen = false">
        <aside class="mobile-drawer" aria-label="Mobile menu">
          <header><div class="mobile-account"><span>{{ session.user?.display_name?.slice(0, 1) || 'J' }}</span><div><b>{{ session.user?.display_name }}</b><small>{{ isPartner ? t("shell.partner") : isClient ? t("shell.client") : session.user?.role === 'staff' ? t("shell.staff") : t("shell.admin") }}</small></div></div><button type="button" :aria-label="t('common.close')" @click="mobileMenuOpen = false"><X :size="21" /></button></header>
          <section v-if="mobileOverflowItems.length" class="drawer-links">
            <button v-for="item in mobileOverflowItems" :key="item.id" :class="{ active: props.view === item.id }" type="button" @click="changeView(item.id)"><component :is="item.icon" :size="19" /><span>{{ item.label }}</span><ChevronRight :size="17" /></button>
          </section>
          <section v-if="isClient && briefs.briefs.length" class="drawer-briefs"><small>{{ t("myBriefs") }}</small><button v-for="brief in briefs.briefs.slice(0, 8)" :key="brief.id" type="button" @click="changeView(`brief:${brief.id}`)"><span>{{ brief.title }}</span><ChevronRight :size="16" /></button></section>
          <footer><label><span>{{ t("shell.language") }}</span><select class="locale" :value="locale" @change="changeLanguage(($event.target as HTMLSelectElement).value)"><option value="zh-CN">中文</option><option value="en">English</option><option value="fr">Francais</option></select></label><button class="drawer-logout" type="button" @click="session.logout"><LogOut :size="18" />{{ t("logout") }}</button></footer>
        </aside>
      </div>
    </Teleport>
  </div>
</template>

<style scoped>
.brief-nav {
  display: grid;
  gap: 4px;
  margin: 18px 4px 0;
  padding-top: 14px;
  border-top: 1px solid var(--line);
}
.brief-nav small {
  padding: 0 6px;
  color: var(--muted);
}
.brief-nav__item {
  overflow: hidden;
  border: 0;
  padding: 8px 6px;
  background: transparent;
  color: var(--ink);
  text-align: left;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.brief-nav__row{display:flex;min-width:0}.brief-nav__row .brief-nav__item{flex:1;min-width:0}.brief-nav__delete{display:none;border:0;background:transparent;color:#b45b51;font-size:11px}.brief-nav__row:hover .brief-nav__delete{display:block}
.brief-nav__item:hover,
.brief-nav__item.active {
  background: #fff0e7;
  color: var(--brand-dark);
}
.shell {
  min-height: 100vh;
  --mobile-topbar-height: 64px;
}
.mobile-header-toggle{display:none}
.mobile-view-title,.mobile-menu-button,.mobile-nav{display:none}
.topbar {
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 22px;
  background: #fff;
  border-bottom: 1px solid var(--line);
  gap: 12px;
}
.brand {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 16px;
}
.brand img {
  width: 30px;
  height: 30px;
}
.topbar__actions {
  display: flex;
  align-items: center;
  gap: 9px;
}
.locale {
  width: auto;
  border: 1px solid var(--line);
  padding: 5px 7px;
  background: #fff;
}
.shell__body {
  display: grid;
  grid-template-columns: 216px minmax(0, 1fr);
  min-height: calc(100vh - 64px);
}
.nav {
  padding: 16px 10px;
  border-right: 1px solid var(--line);
  background: #fbfcfc;
}
.nav__item {
  display: flex;
  align-items: center;
  gap: 9px;
  width: 100%;
  border: 0;
  padding: 10px;
  text-align: left;
  background: transparent;
  color: var(--ink);
}
.nav__item.active {
  background: #fff0e7;
  color: var(--brand-dark);
  font-weight: 700;
}
.main {
  min-width: 0;
}
.user-name {
  font-size: 13px;
}
.logout{display:inline-flex;align-items:center;gap:6px}
@media (max-width: 700px) {
  .shell{padding-bottom:calc(62px + env(safe-area-inset-bottom));--mobile-topbar-height:56px}
  .topbar {
    height: auto;
    min-height: 56px;
    position:sticky;
    top:0;
    z-index:30;
    padding:8px 12px;
    display:grid;
    grid-template-columns:36px minmax(0,1fr) 36px;
  }
  .brand{min-width:0}.brand img{width:30px;height:30px}.brand b{display:none}
  .mobile-view-title{display:block;overflow:hidden;text-align:center;text-overflow:ellipsis;white-space:nowrap;font-size:16px}
  .mobile-menu-button{display:grid;width:36px;height:36px;place-items:center;border:0;background:transparent;color:var(--ink)}
  .topbar>.topbar__actions{display:none}
  .shell--detail{--mobile-topbar-height:32px}
  .shell--detail .topbar{height:32px;min-height:32px;justify-content:center;padding:0 8px}
  .shell--detail .brand,.shell--detail .topbar__actions,.shell--detail .mobile-menu-button{display:none}
  .mobile-header-toggle{display:grid;width:40px;height:32px;place-items:center;border:0;background:transparent;color:var(--brand-dark)}
  .shell--detail.shell--header-expanded{--mobile-topbar-height:56px}
  .shell--detail.shell--header-expanded .topbar{display:grid;grid-template-columns:40px minmax(0,1fr) 40px;height:auto;min-height:56px;justify-content:initial;padding:6px 10px}
  .shell--detail.shell--header-expanded .mobile-view-title{display:block}.shell--detail.shell--header-expanded .mobile-menu-button{display:grid}
  .user-name {
    display: none;
  }
  .shell__body {
    grid-template-columns: 1fr;
    min-height:calc(100dvh - var(--mobile-topbar-height) - 62px - env(safe-area-inset-bottom));
  }
  .nav--desktop{display:none}
  .mobile-nav {
    position:fixed;z-index:40;left:0;right:0;bottom:0;
    display:grid;
    grid-template-columns:repeat(var(--mobile-nav-count),minmax(0,1fr));
    gap:0;
    min-height:calc(62px + env(safe-area-inset-bottom));
    padding:5px 6px env(safe-area-inset-bottom);
    border:0;
    border-top:1px solid var(--line);
    background:#fff;
    box-shadow:0 -8px 24px rgba(24,34,44,.08);
  }
  .mobile-nav__item {
    display:grid;
    width:100%;
    min-width:0;
    min-height:52px;
    place-items:center;
    align-content:center;
    gap:2px;
    padding:4px 2px;border:0;background:transparent;color:#68746f;
    font-size:11px;
  }
  .mobile-nav__item.active{background:transparent;color:var(--brand-dark);font-weight:700}
  .mobile-nav__item span{overflow:hidden;max-width:100%;text-overflow:ellipsis;white-space:nowrap}
  .brief-nav{display:none}
  .topbar__actions {
    gap: 6px;
  }
  .topbar__actions .btn {
    width:38px;
    min-height:38px;
    padding:0;
    justify-content:center;
  }
  .logout span,.topbar__actions>.status{display:none}
  .locale {
    max-width: 74px;
  }
  .main{min-height:calc(100dvh - var(--mobile-topbar-height) - 62px - env(safe-area-inset-bottom))}
}
.mobile-drawer-backdrop{position:fixed;z-index:120;inset:0;background:rgba(18,27,24,.38)}
.mobile-drawer{position:absolute;top:0;right:0;display:flex;flex-direction:column;width:min(86vw,360px);height:100dvh;padding:calc(14px + env(safe-area-inset-top)) 14px calc(18px + env(safe-area-inset-bottom));background:#fff;box-shadow:-16px 0 50px rgba(17,29,25,.2)}
.mobile-drawer>header{display:flex;align-items:center;justify-content:space-between;padding:4px 2px 18px;border-bottom:1px solid var(--line)}.mobile-drawer>header>button{display:grid;width:38px;height:38px;place-items:center;border:0;background:transparent}.mobile-account{display:flex;align-items:center;gap:11px}.mobile-account>span{display:grid;width:42px;height:42px;place-items:center;border-radius:50%;background:#263d36;color:#fff;font-weight:800}.mobile-account div{display:grid}.mobile-account small{color:var(--muted)}
.drawer-links,.drawer-briefs{display:grid;padding:12px 0;border-bottom:1px solid var(--line)}.drawer-links button,.drawer-briefs button{display:grid;grid-template-columns:24px minmax(0,1fr) 18px;align-items:center;gap:9px;min-height:48px;border:0;padding:8px 10px;background:transparent;text-align:left}.drawer-links button.active{background:#fff3eb;color:var(--brand-dark)}.drawer-briefs{overflow-y:auto}.drawer-briefs>small{padding:5px 10px;color:var(--muted);font-weight:700}.drawer-briefs button{grid-template-columns:minmax(0,1fr) 18px}.drawer-briefs button span{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.mobile-drawer>footer{display:grid;gap:10px;margin-top:auto;padding-top:16px}.mobile-drawer>footer label{display:flex;align-items:center;justify-content:space-between}.drawer-logout{display:flex;align-items:center;justify-content:center;gap:7px;min-height:44px;border:1px solid #efc5c0;background:#fff;color:#9b3f36}
@media(min-width:701px){.mobile-drawer-backdrop{display:none}}
</style>
